import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, Download, Maximize2, Minimize2, Loader2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { authorizedFetch, noAuthorizedFetch, getAccessToken } from '../utils/api';
import FileTypeIcon from './FileTypeIcon';
import ZipViewer from './ZipViewer';
import * as XLSX from 'xlsx';

async function downloadBlob(url, filename) {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
}

function useFileContent(file, sessionId) {
    const [content, setContent] = useState(null);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setContent(null);
        setDownloadUrl(null);

        async function load() {
            try {
                const safeFetch = getAccessToken() ? authorizedFetch : noAuthorizedFetch;
                const data = await safeFetch(
                    `/v2/sessions/${sessionId}/files/content?file_path=${encodeURIComponent(file.path)}`
                );
                if (cancelled) return;
                if (data.content) setContent(data.content);
                if (data.download_url) setDownloadUrl(data.download_url);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [file, sessionId]);

    return { content, downloadUrl, loading, error };
}

const DOWNLOAD_OPTIONS = [
    { type: 'md', label: 'Markdown', color: '#4285F4', letter: 'M' },
    { type: 'pdf', label: 'PDF', color: '#E5252A', letter: 'A' },
    { type: 'docx', label: 'Docx', color: '#2B579A', letter: 'W' },
];

function DownloadMenu({ file, content, downloadUrl, sessionId }) {
    const [open, setOpen] = useState(false);
    const [converting, setConverting] = useState(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleDownload = async (type) => {
        setOpen(false);
        if (type === 'md') {
            if (downloadUrl) {
                await downloadBlob(downloadUrl, file.filename);
            } else if (content) {
                const blob = new Blob([content], { type: 'text/markdown' });
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = file.filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(blobUrl);
            }
            return;
        }
        setConverting(type);
        try {
            const data = await authorizedFetch(
                `/v2/sessions/${sessionId}/files/convert`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        file_path: file.path,
                        convert_type: type,
                    }),
                }
            );
            if (data.download_url) {
                await downloadBlob(data.download_url, data.filename || `${file.filename}.${type}`);
            }
        } catch (err) {
            console.error('Convert failed:', err);
        } finally {
            setConverting(null);
        }
    };

    return (
        <div ref={menuRef} className="relative">
            <button
                onClick={() => setOpen(v => !v)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Download"
            >
                {converting ? (
                    <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                ) : (
                    <Download className="w-4 h-4 text-gray-500" />
                )}
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px] z-10">
                    {DOWNLOAD_OPTIONS.map(opt => (
                        <button
                            key={opt.type}
                            onClick={() => handleDownload(opt.type)}
                            disabled={!!converting}
                            className="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            <span
                                className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                style={{ backgroundColor: opt.color }}
                            >
                                {opt.letter}
                            </span>
                            <span>{opt.label}</span>
                            {converting === opt.type && (
                                <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin ml-auto" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function colLabel(idx) {
    let label = '';
    let n = idx;
    while (n >= 0) {
        label = String.fromCharCode(65 + (n % 26)) + label;
        n = Math.floor(n / 26) - 1;
    }
    return label;
}

function SpreadsheetRenderer({ downloadUrl, csvContent }) {
    const [workbook, setWorkbook] = useState(null);
    const [activeSheet, setActiveSheet] = useState(0);
    const [loading, setLoading] = useState(!csvContent);
    const [parseError, setParseError] = useState(null);
    const [selectedCell, setSelectedCell] = useState({ r: 0, c: 0 });

    useEffect(() => {
        if (csvContent != null) {
            try {
                const wb = XLSX.read(csvContent, { type: 'string' });
                setWorkbook(wb);
            } catch (err) {
                setParseError(err.message);
            }
            setLoading(false);
            return;
        }
        if (!downloadUrl) return;
        let cancelled = false;
        setLoading(true);

        async function parse() {
            try {
                const res = await fetch(downloadUrl);
                const buf = await res.arrayBuffer();
                const wb = XLSX.read(buf, { type: 'array' });
                if (!cancelled) setWorkbook(wb);
            } catch (err) {
                if (!cancelled) setParseError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        parse();
        return () => { cancelled = true; };
    }, [downloadUrl, csvContent]);

    const sheetData = useMemo(() => {
        if (!workbook) return null;
        const name = workbook.SheetNames[activeSheet];
        const sheet = workbook.Sheets[name];
        if (!sheet) return null;

        const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
        const totalCols = Math.max(range.e.c + 1, 1);
        const totalRows = Math.max(range.e.r + 1, 1);
        const displayCols = Math.max(totalCols, 10);
        const displayRows = Math.max(totalRows, 20);

        const rows = [];
        for (let r = 0; r < displayRows; r++) {
            const row = [];
            for (let c = 0; c < displayCols; c++) {
                const addr = XLSX.utils.encode_cell({ r, c });
                const cell = sheet[addr];
                row.push(cell ? XLSX.utils.format_cell(cell) : '');
            }
            rows.push(row);
        }
        return { rows, totalCols: displayCols, totalRows: displayRows };
    }, [workbook, activeSheet]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
        );
    }

    if (parseError || !sheetData) {
        return (
            <div className="flex items-center justify-center py-20 text-sm text-red-500">
                {parseError || 'Failed to parse spreadsheet'}
            </div>
        );
    }

    const selectedAddr = colLabel(selectedCell.c) + (selectedCell.r + 1);
    const selectedValue = sheetData.rows[selectedCell.r]?.[selectedCell.c] || '';

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="w-14 text-center text-xs font-medium text-gray-600 border border-gray-300 rounded px-1.5 py-1 bg-white">
                    {selectedAddr}
                </div>
                <div className="flex-1 text-xs text-gray-700 border border-gray-300 rounded px-2 py-1 bg-white truncate">
                    {selectedValue}
                </div>
            </div>

            <div className="flex-1 overflow-auto">
                <table className="border-collapse w-max min-w-full">
                    <thead className="sticky top-0 z-10">
                        <tr>
                            <th className="w-12 min-w-[48px] h-7 bg-gray-100 border border-gray-300 text-[11px] text-gray-500 font-medium sticky left-0 z-20" />
                            {Array.from({ length: sheetData.totalCols }, (_, c) => (
                                <th
                                    key={c}
                                    className={`min-w-[80px] h-7 border border-gray-300 text-[11px] font-medium px-1 ${
                                        selectedCell.c === c ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                    }`}
                                >
                                    {colLabel(c)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sheetData.rows.map((row, r) => (
                            <tr key={r}>
                                <td
                                    className={`w-12 min-w-[48px] h-7 border border-gray-300 text-[11px] font-medium text-center sticky left-0 z-10 ${
                                        selectedCell.r === r ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                    }`}
                                >
                                    {r + 1}
                                </td>
                                {row.map((cell, c) => {
                                    const isSelected = selectedCell.r === r && selectedCell.c === c;
                                    return (
                                        <td
                                            key={c}
                                            onClick={() => setSelectedCell({ r, c })}
                                            className={`min-w-[80px] h-7 border text-[12px] text-gray-800 px-1.5 cursor-cell whitespace-nowrap ${
                                                isSelected
                                                    ? 'border-2 border-blue-500 bg-white outline-none'
                                                    : 'border-gray-200 bg-white hover:bg-blue-50/30'
                                            }`}
                                        >
                                            {cell}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {workbook && workbook.SheetNames.length > 0 && (
                <div className="flex items-center border-t border-gray-300 bg-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-1 px-2 py-1">
                        {workbook.SheetNames.map((name, idx) => (
                            <button
                                key={name}
                                onClick={() => { setActiveSheet(idx); setSelectedCell({ r: 0, c: 0 }); }}
                                className={`px-3 py-1 text-xs rounded-t border border-b-0 transition-colors ${
                                    idx === activeSheet
                                        ? 'bg-white text-gray-800 font-medium border-gray-300'
                                        : 'bg-gray-50 text-gray-500 border-transparent hover:bg-gray-200'
                                }`}
                            >
                                {name}
                            </button>
                        ))}
                    </div>
                    <div className="flex-1" />
                    <div className="px-3 py-1 text-[11px] text-gray-500">Ready</div>
                </div>
            )}
        </div>
    );
}

function ContentRenderer({ file, content, downloadUrl }) {
    const iconType = file.icon_type;

    if (iconType === 'zip' && downloadUrl) {
        return <ZipViewer downloadUrl={downloadUrl} />;
    }

    if (['xlsx', 'xls'].includes(iconType) && downloadUrl) {
        return <SpreadsheetRenderer downloadUrl={downloadUrl} />;
    }

    if (iconType === 'csv' && content) {
        return <SpreadsheetRenderer csvContent={content} />;
    }

    if (iconType === 'md') {
        return (
            <div className="prose prose-sm max-w-none px-8 py-6 fv-markdown">
                <style>{`
                    .fv-markdown table { border-collapse: collapse; width: 100%; background: white; border-radius: 8px; overflow: hidden; border: 1px solid #e5e7eb; }
                    .fv-markdown th { background: #f9fafb; font-weight: 600; text-align: left; padding: 10px 16px; border: 1px solid #e5e7eb; font-size: 13px; color: #374151; }
                    .fv-markdown td { padding: 10px 16px; border: 1px solid #e5e7eb; font-size: 13px; color: #4b5563; }
                    .fv-markdown tr:hover td { background: #f9fafb; }
                `}</style>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ''}</ReactMarkdown>
            </div>
        );
    }

    if (['txt', 'json', 'code'].includes(iconType)) {
        return (
            <pre className="px-6 py-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto">
                {content || ''}
            </pre>
        );
    }

    if (iconType === 'pdf' && downloadUrl) {
        return (
            <iframe
                src={downloadUrl}
                className="w-full h-full border-0"
                title={file.filename}
            />
        );
    }

    if (iconType === 'image' && downloadUrl) {
        return (
            <div className="flex items-center justify-center p-6">
                <img
                    src={downloadUrl}
                    alt={file.filename}
                    className="max-w-full max-h-[60vh] object-contain rounded-lg"
                />
            </div>
        );
    }

    if (downloadUrl) {
        return (
            <div className="flex flex-col items-center justify-center gap-5 py-20">
                <div className="flex flex-col items-center gap-3">
                    <FileTypeIcon iconType={file.icon_type} size={56} />
                    <div className="text-center">
                        <p className="text-sm font-medium text-gray-800">{file.filename}</p>
                        <p className="text-xs text-gray-400 mt-0.5 uppercase">{file.icon_type} File</p>
                    </div>
                </div>
                <p className="text-sm text-gray-500 text-center leading-relaxed">
                    Không hỗ trợ xem trước định dạng này.<br />
                    Vui lòng tải file về để xem nội dung.
                </p>
                <button
                    onClick={() => downloadBlob(downloadUrl, file.filename)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors"
                >
                    <Download className="w-4 h-4" />
                    Tải xuống
                </button>
            </div>
        );
    }

    if (content) {
        return (
            <pre className="px-6 py-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">
                {content}
            </pre>
        );
    }

    return (
        <div className="flex items-center justify-center py-20 text-sm text-gray-400">
            Không có nội dung
        </div>
    );
}

export default function FileViewer({ file, sessionId, onClose }) {
    const isPdf = file.icon_type === 'pdf';
    const isSpreadsheet = ['xlsx', 'xls', 'csv'].includes(file.icon_type);
    const isZip = file.icon_type === 'zip';
    const isMd = file.icon_type === 'md';
    const [closing, setClosing] = useState(false);
    const [expanded, setExpanded] = useState(isPdf);
    const { content, downloadUrl, loading, error } = useFileContent(file, sessionId);

    const handleClose = useCallback(() => {
        setClosing(true);
        setTimeout(onClose, 200);
    }, [onClose]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleClose]);

    const modalSize = expanded
        ? 'w-screen h-screen rounded-none'
        : 'w-[1200px] max-w-[95vw] h-[85vh] rounded-xl';

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <style>{`
                @keyframes fvOverlayIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes fvOverlayOut { from { opacity: 1; } to { opacity: 0; } }
                @keyframes fvZoomIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
                @keyframes fvZoomOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.92); } }
            `}</style>
            <div
                className="absolute inset-0 bg-black/40"
                style={{ animation: `${closing ? 'fvOverlayOut' : 'fvOverlayIn'} 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards` }}
                onClick={handleClose}
            />
            <div
                className={`relative ${modalSize} bg-white shadow-2xl flex flex-col`}
                style={{ animation: `${closing ? 'fvZoomOut' : 'fvZoomIn'} 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards` }}
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <FileTypeIcon iconType={file.icon_type} size={24} />
                        <span className="text-sm font-semibold text-gray-800 truncate">
                            {file.filename}
                        </span>
                        {file.source === 'upload' && (
                            <span className="text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded uppercase flex-shrink-0">
                                upload
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        {isMd ? (
                            <DownloadMenu
                                file={file}
                                content={content}
                                downloadUrl={downloadUrl}
                                sessionId={sessionId}
                            />
                        ) : downloadUrl && (
                            <button
                                onClick={() => downloadBlob(downloadUrl, file.filename)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Download"
                            >
                                <Download className="w-4 h-4 text-gray-500" />
                            </button>
                        )}
                        <button
                            onClick={() => setExpanded(v => !v)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title={expanded ? 'Collapse' : 'Expand'}
                        >
                            {expanded
                                ? <Minimize2 className="w-4 h-4 text-gray-500" />
                                : <Maximize2 className="w-4 h-4 text-gray-500" />
                            }
                        </button>
                        <button
                            onClick={handleClose}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    </div>
                </div>

                <div className={`flex-1 ${isSpreadsheet || isZip ? '' : 'overflow-y-auto'}`}>
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center py-20 text-sm text-red-500">
                            {error}
                        </div>
                    ) : (
                        <ContentRenderer file={file} content={content} downloadUrl={downloadUrl} />
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
